import type { ReactElement, ReactNode, UIEvent } from 'react';
import { useCallback, useMemo } from 'react';
import type { Proposal } from '@shared/types';
import type { LivingBriefDetail, ReviewItem } from '@shared/api';
import { MissingPriceNote } from './MissingPriceNote';
import { PageCard } from './PageCard';
import { PendingPageCard } from './PendingPageCard';
import { ValidationBanner } from './ValidationBanner';
import { sectionIdForProduct } from '../lib/plan';
import { openReviewItemsForPage } from '../lib/reviewItems';
import { mergeSequence, type PendingPagesState } from '../state/pendingPages';

/**
 * The permanent proposal surface. Build, idle and agent-update modes all use
 * this same ordered contact sheet geometry; temporary state is applied to the
 * individual slide without replacing the deck.
 */
/** Stable default: a fresh `[]` per render would re-run every memo/effect keyed on it. */
const NO_PENDING_PAGES: PendingPagesState = [];

export function GridWorkspace({
  proposal,
  updatingIds,
  highlightedIds,
  newlyAddedIds,
  buildingIds,
  pendingPages = NO_PENDING_PAGES,
  updating,
  meta,
  initialScrollTop,
  onScrollPositionChange,
  onRegionReady,
  onFocus,
  reviewItems = [],
  onReview,
  details = [],
  stageTracker,
}: {
  proposal: Proposal;
  updatingIds: string[];
  highlightedIds: string[];
  newlyAddedIds: string[];
  buildingIds: string[];
  /** docs/custom-page.md "Pending page": placeholders spliced into the grid at their final position. */
  pendingPages?: PendingPagesState;
  updating: boolean;
  meta: string;
  initialScrollTop: number;
  onScrollPositionChange: (scrollTop: number) => void;
  onRegionReady?: (element: HTMLDivElement | null) => void;
  onFocus: (sectionId: string) => void;
  reviewItems?: ReviewItem[];
  onReview?: (sectionId: string) => void;
  details?: LivingBriefDetail[];
  /**
   * G2 (docs live-build gap follow-up): a compact `StageTracker` strip,
   * rendered above the page grid for as long as the build stays in flight
   * after the skeleton document lands — see `isBuildInFlight` in
   * `web/src/lib/buildRail.ts`. `undefined`/`null` renders nothing.
   */
  stageTracker?: ReactNode;
}): ReactElement {
  const allSections = proposal.document?.sections ?? [];
  const updatingSet = useMemo(() => new Set(updatingIds), [updatingIds]);
  const highlightedSet = useMemo(() => new Set(highlightedIds), [highlightedIds]);
  const newlyAddedSet = useMemo(() => new Set(newlyAddedIds), [newlyAddedIds]);
  const buildingSet = useMemo(() => new Set(buildingIds), [buildingIds]);
  const gridItems = useMemo(
    () => mergeSequence(allSections, pendingPages),
    [allSections, pendingPages],
  );

  const setRegion = useCallback((element: HTMLDivElement | null) => {
    if (element) element.scrollTop = initialScrollTop;
    onRegionReady?.(element);
  }, [initialScrollTop, onRegionReady]);

  const rememberScroll = (event: UIEvent<HTMLDivElement>) => {
    onScrollPositionChange(event.currentTarget.scrollTop);
  };

  return (
    <div
      className="proposal-grid-region"
      ref={setRegion}
      onScroll={rememberScroll}
    >
      <div className="proposal-grid-surface">
        {stageTracker}
        <ValidationBanner validation={proposal.validation} />
        <MissingPriceNote
          proposal={proposal}
          onFocusProduct={(productId) => {
            const sectionId = sectionIdForProduct(proposal, productId);
            if (sectionId) onFocus(sectionId);
          }}
        />
        <div className="proposal-grid" role="list" aria-label={updating ? 'Proposal pages updating' : `Proposal pages, ${meta}`}>
          {gridItems.map((item) => item.kind === 'pending' ? (
            <PendingPageCard key={item.key} entry={item.entry} />
          ) : (
            <PageCard
              key={item.key}
              proposal={proposal}
              section={item.value}
              updating={updatingSet.has(item.key)}
              recentlyChanged={highlightedSet.has(item.key)}
              newlyAdded={newlyAddedSet.has(item.key)}
              building={buildingSet.has(item.key)}
              placeholder={details.find((detail) => detail.status === 'missing' && detail.affectedPageIds.includes(item.key))?.placeholder}
              reviewCount={openReviewItemsForPage(reviewItems, item.key).length}
              onReview={onReview}
              onFocus={onFocus}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
